<script setup>
import { Link, Head, useForm } from "@inertiajs/inertia-vue3";
import { Inertia } from "@inertiajs/inertia";
import Navbar from "@/Components/Navbar.vue";
import Modal from "@/Components/Calendar/Modal.vue";
import SearchModal from "@/Components/Calendar/SlotSearchModal.vue";
import Button from "@/Components/Button.vue";
import Select from "@/Components/Select.vue";
import Textarea from "@/Components/Textarea.vue";
import StatiSearch from "@/Components/Calendar/StatiSearch.vue";
import Label from "@/Components/Settings/Label.vue";
import Input from "@/Components/Settings/Input.vue";
import HeaderLink from "@/Components/Settings/HeaderLink.vue";
import HeaderButton from "@/Components/Settings/HeaderButton.vue";
import { ref, onMounted, watch } from "vue";

import PatientSearch from "@/Components/Calendar/PatientSearch.vue";
import VueTimepicker from "vue3-timepicker";

import axios from "axios";
import debounce from "lodash/debounce";

const props = defineProps({
  errors: Object,
  practitioners: Array,
  rooms: Array,
  interval: Number,
  types: Array,
  date: String,
  time: String,
  behandler: String,
  raum: String,
  office: Object,
  stati: Array,
  interval_values: Object,
  is_parallel: Boolean,
  ignore: Boolean,
});

const form = useForm({
  date: props.date,
  start: props.time || "15:00",
  duration: "",
  practitioner_id: props.behandler || "",
  room_id: props.raum || "",
  type_id: "",
  patient_number: null,
  patient_firstname: "",
  patient_lastname: "",
  birthday: "",
  tel: "",
  email: "",
  description: "",
  status: 0,
  equipment: "",
  send_mail_on_create: false,
  send_sms_on_create: false,
  customPatient: false,
  is_parallel: props.is_parallel,
  print: false,
  ignore_warnings: false,
});

const patientResults = ref([]);
const showSearchModal = ref(false);
const modalIsPast = ref(false);
const modalData = ref({
  show: false,
  type: "",
  message: "",
});
const patientNumber = ref("");
const isLoading = ref(false);
const type = ref();
const disabledRooms = ref([]);
const showSMS = ref(false);
const initialPractitioner = props.behandler || false;

function checkIfTimeSlotIsInPast() {
  let now = new Date();
  let startDateTime = new Date(form.date + " " + form.start);
  if (startDateTime < now) {
    modalIsPast.value = true;
  } else {
    modalIsPast.value = false;
  }
}

onMounted(() => {
  checkIfTimeSlotIsInPast();
  // console.log("mounted", form);
});

// watch form.date and form.start to check if time slot is in past
watch(
  () => form.date,
  (value) => {
    checkIfTimeSlotIsInPast();
  }
);
watch(
  () => form.start,
  (value) => {
    checkIfTimeSlotIsInPast();
  }
);

// watch form tel to show sms checkbox
watch(
  () => form.tel,
  (value) => {
    showSMS.value =
      value && props.office.anysms_id && props.office.anysms_token;
  }
);

watch(
  patientNumber,
  debounce((val) => {
    //TODO: before debounce
    form.reset(
      "patient_number",
      "patient_firstname",
      "patient_lastname",
      "birthday",
      "email",
      "tel"
    );
    // form.patient_number = null;
    checkPatientNumber();
    // form.patient_number = patientNumber.value;
  }, 500)
);

function changeType() {
  let activeType = props.types.find((t) => {
    return t.id == form.type_id;
  });

  //console.log(activeType);

  // form.duration === null &&  TODO: should duration only be automatically set if it was not set before by the user
  if (activeType.duration !== null) {
    form.duration = activeType.duration;
  }
  form.equipment = activeType.materials;

  form.practitioner_id = "";
  if (activeType.practitioners.length == 1) {
    if (activeType.practitioners[0].id) {
      form.practitioner_id = activeType.practitioners[0].id;
    }
  } else if (
    (activeType.practitioners.length == 0 ||
      activeType.practitioners.length > 1) &&
    initialPractitioner
  ) {
    form.practitioner_id = initialPractitioner;
  }

  //console.log("res", activeType);
}

function storeAndPrint() {
  form.print = true;
  storeAppointment();
}

function storeAndNoPrint() {
  form.print = false;
  storeAppointment();
}

function storeAppointment(ignore_warnings = false) {
  isLoading.value = true;

  form.ignore_warnings = ignore_warnings || props.ignore;
  //console.log("store", form);
  form.post("/appointments", {
    onFinish: (data) => {
      form.ignore_warnings = false;
      isLoading.value = false;
    },
    onError: (errors) => {
      if (errors.showModal) {
        modalData.value.show = true;
        modalData.value.type = errors.type;
        modalData.value.message = errors.message;
      }
    },
  });
}

function toggleCustomPatientInput() {
  if (form.customPatient) {
    form.customPatient = false;
    form.reset("patient_number");
  } else {
    form.customPatient = true;

    form.reset(
      "patient_number",
      "patient_firstname",
      "patient_lastname",
      "birthday",
      "email",
      "tel"
    );
  }
}

function selectPatient(patient) {
  form.patient_number = patient.patient_number;
  form.patient_firstname = patient.firstname;
  form.patient_lastname = patient.lastname;
  form.birthday = patient.birthday
    ? new Date(patient.birthday).toISOString().substr(0, 10)
    : null;
  form.email = patient.email;
  form.tel = patient.tel;
}

//function selectStati(status) {
//    console.log('STATUS', status);
//}

function checkPatientNumber() {
  axios
    .get("/patients/search", {
      params: {
        patient_number: patientNumber.value,
      },
    })
    .then((response) => {
      //console.log("patient_response", response);
      if (response.data.length > 0) {
        patientResults.value = response.data;
        // form.patient_firstname = response.data[0].firstname;
        // form.patient_lastname = response.data[0].lastname;
      }
    })
    .catch((err) => {
      console.error("patient_err", err);
    });
}

function chooseSlot(slot) {
  //console.log("slot", slot);
  showSearchModal.value = false;
  form.date = slot.date;
  form.start = slot.startString;
}

// check room
watch(
  () => form.date,
  (cur, old) => {
    if (cur !== old) {
      checkRoomOccupation();
    }
  }
);
watch(
  () => form.start,
  (cur, old) => {
    if (cur !== old) {
      checkRoomOccupation();
    }
  }
);
watch(
  () => form.duration,
  (cur, old) => {
    if (cur !== old) {
      checkRoomOccupation();
    }
  }
);
function checkRoomOccupation() {
  let re = /^\d{2}:\d{2}$/; // 00:00
  if (form.date && form.start && re.test(form.start) && form.duration) {
    axios
      .get("/room/occupation", {
        params: {
          date: form.date,
          start: form.start,
          duration: form.duration,
          app_id: 0,
        },
      })
      .then((resp) => {
        disabledRooms.value = resp.data.occupations;

        if (resp.data.occupations.includes(parseInt(form.room_id))) {
          form.reset("room_id");
        }
      })
      .catch((err) => {
        console.error("checkRoomOccupation error", err);
      });
  }
}
</script>

<template>
  <div
    id="theme-wrapper"
    style="background-image: url('/storage/img/auth_bg.jpg')"
    class="bg-cover bg-no-repeat flex h-screen bg-oteoBlue-600"
    :class="'theme-' + $page.props.theme ?? 'spitta'"
  >
    <Head title="Termin erstellen" />
    <Navbar class="navbar-sub w-full" />

    <SearchModal
      v-if="showSearchModal"
      :practitioners="props.practitioners"
      :rooms="props.rooms"
      :practitioner_id="form.practitioner_id"
      :room_id="form.room_id"
      :duration="form.duration"
      :start="form.date"
      :end="form.date"
      :interval="props.interval"
      @close="showSearchModal = false"
      @chooseSlot="chooseSlot"
    ></SearchModal>

    <Modal :show="modalIsPast" @close="modalIsPast = false">
      <template #title>
        <div>Warnung</div>
      </template>
      <template #message>
        <p>
          Termin liegt in der Vergangenheit. Bitte wählen Sie einen anderen
          Termin.
        </p>
      </template>
      <template #actions>
        <div class="">
          <div class="flex gap-5 justify-center mt-8">
            <Button
              class="mt-auto"
              @click="
                () => {
                  Inertia.get('/kalender');
                }
              "
              >Zurück zum Kalender</Button
            >

            <Button
              type="button"
              class="mt-auto"
              @click="
                () => {
                  modalIsPast = false;
                }
              "
              >Dennoch fortfahren</Button
            >
          </div>
        </div>
      </template>
    </Modal>

    <Modal :show="modalData.show" @close="modalData.show = false">
      <template #title>
        <div v-if="modalData.type === 'warning'">Warnung</div>
        <div v-if="modalData.type === 'error'">Fehler</div>
      </template>
      <template #message>
        <!-- {{ modalData.message }} -->
        <p v-html="modalData.message"></p>
      </template>
      <template #actions>
        <div class="" v-if="modalData.type === 'warning'">
          <div class="flex gap-5 justify-center mt-8">
            <Button
              class="mt-auto"
              @click="
                () => {
                  modalData.show = false;
                  showSearchModal = true;
                }
              "
              >Alternativer Termin</Button
            >

            <Button
              type="button"
              :disabled="isLoading"
              class="mt-auto"
              @click="storeAppointment(true)"
              >Termin übernehmen</Button
            >
          </div>
        </div>

        <div class="" v-if="modalData.type === 'error'">
          <div class="flex gap-5 justify-center mt-8">
            <Button
              class="mt-auto"
              @click="
                () => {
                  modalData.show = false;
                  showSearchModal = true;
                }
              "
              >Alternativer Termin</Button
            >

            <Button class="mt-auto" @click="modalData.show = false"
              >Abbrechen</Button
            >
          </div>
        </div>
      </template>
    </Modal>

    <div class="bg-white w-full max-w-8xl m-auto rounded-xl">
      <header
        class="bg-gray-100 flex justify-between items-center px-12 py-8 rounded-tl-xl rounded-tr-xl relative"
      >
        <div class="flex">
          <Link href="/termin-erstellen">
            <h2 class="text-3xl font-semibold py-3 mr-8">
              Einzeltermin
              <!-- <span class="font-light">/ <slot name="breadcrumb" /></span> -->
            </h2>
          </Link>
          <Link
            :href="
              '/termin-erstellen?typ=serie&date=' +
              props.date +
              '&behandler=' +
              props.behandler +
              (props.ignore ? '&ignore=true' : '')
            "
          >
            <h2 class="text-3xl font-light py-3 opacity-40">
              Terminserie
              <!-- <span class="font-light">/ <slot name="breadcrumb" /></span> -->
            </h2>
          </Link>
        </div>

        <div
          class="w-16 overflow-hidden inline-block absolute left-28 bottom-0"
        >
          <div
            class="h-8 w-11 bg-white rotate-45 transform origin-bottom-left"
          ></div>
        </div>
        <div class="flex relative">
          <!-- <slot name="controls" /> -->
          <HeaderLink
            class="mr-3"
            :href="'/kalender' /*?datum=' + props.date*/"
            type="close"
            >Abbrechen</HeaderLink
          >
          <HeaderButton
            :disabled="isLoading"
            class="mr-3"
            @click="storeAndPrint()"
            >Anlegen und Drucken</HeaderButton
          >
          <HeaderButton :disabled="isLoading" @click="storeAndNoPrint()"
            >Anlegen</HeaderButton
          >
        </div>
      </header>
      <div class="flex p-12 relative">
        <div class="md:flex-1">
          <div class="flex">
            <div class="pr-16 pb-8 w-[35%]">
              <div class="">
                <Label for="patient_number" value="Patient suchen" />
                <div class="flex gap-4 mt-1 relative">
                  <PatientSearch
                    class="z-[9]"
                    :class="{
                      'opacity-60': form.customPatient,
                    }"
                    :disabled="form.customPatient"
                    @selectPatient="selectPatient"
                  />

                  <div
                    class="absolute right-0 -bottom-5 text-xs font-semibold text-oteo-300 cursor-pointer"
                    @click="toggleCustomPatientInput"
                  >
                    <span v-if="!form.customPatient">
                      Oder manuell eingeben.
                    </span>
                    <span v-if="form.customPatient"> Patient/in suchen. </span>
                  </div>
                </div>
                <div
                  class="mt-2 text-red-600 text-sm"
                  v-if="errors.patient_number"
                >
                  {{ errors.patient_number }}
                </div>
              </div>
              <div class="mt-8" :class="{ 'opacity-60': !form.customPatient }">
                <div class="">
                  <Label for="patient_firstname" value="Vorname" />
                  <Input
                    :disabled="!form.customPatient"
                    id="patient_firstname"
                    type="text"
                    class="mt-1 block w-full"
                    v-model="form.patient_firstname"
                    required
                    placeholder=""
                  />
                  <div
                    class="mt-2 text-red-600 text-sm"
                    v-if="errors.patient_firstname"
                  >
                    {{ errors.patient_firstname }}
                  </div>
                </div>

                <div class="mt-8">
                  <Label for="patient_lastname" value="Nachname" />
                  <Input
                    :disabled="!form.customPatient"
                    id="patient_lastname"
                    type="text"
                    class="mt-1 block w-full"
                    v-model="form.patient_lastname"
                    required
                    placeholder=""
                  />
                  <div
                    class="mt-2 text-red-600 text-sm"
                    v-if="errors.patient_lastname"
                  >
                    {{ errors.patient_lastname }}
                  </div>
                </div>

                <div class="mt-8">
                  <Label for="birthday" value="Geburtstag" />
                  <Input
                    :disabled="!form.customPatient"
                    id="birthday"
                    type="date"
                    class="mt-1 block w-full"
                    v-model="form.birthday"
                    placeholder=""
                  />
                  <div class="mt-2 text-red-600 text-sm" v-if="errors.birthday">
                    {{ errors.birthday }}
                  </div>
                </div>

                <div class="mt-8 flex gap-4">
                  <div class="w-full">
                    <Label for="tel">
                      Handy
                      <span class="font-light">(für Terminservice)</span>
                    </Label>
                    <Input
                      :disabled="!form.customPatient"
                      id="tel"
                      type="tel"
                      class="mt-1 block w-full"
                      v-model="form.tel"
                      required
                      placeholder=""
                    />
                    <div class="mt-2 text-red-600 text-sm" v-if="errors.tel">
                      {{ errors.tel }}
                    </div>
                  </div>

                  <div class="w-full">
                    <Label for="email">
                      E-Mail
                      <span class="font-light">(für Terminservice)</span>
                    </Label>
                    <Input
                      :disabled="!form.customPatient"
                      id="email"
                      type="email"
                      class="mt-1 block w-full"
                      v-model="form.email"
                      required
                      placeholder=""
                    />
                    <div class="mt-2 text-red-600 text-sm" v-if="errors.email">
                      {{ errors.email }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="w-[35%] px-16 border-x border-gray-200 pb-8">
              <div class="">
                <Label for="date" value="Datum" />
                <Input
                  id="date"
                  type="date"
                  class="mt-1 block w-full"
                  v-model="form.date"
                  required
                  placeholder=""
                />
                <div class="mt-2 text-red-600 text-sm" v-if="errors.date">
                  {{ errors.date }}
                </div>
              </div>

              <div class="mt-8">
                <Label for="start" value="Uhrzeit" />

                <VueTimepicker
                  class="timepicker-input"
                  v-model="form.start"
                  :key="form.start"
                  :minute-interval="props.interval"
                  format="HH:mm"
                  close-on-complete
                  fixed-dropdown-button
                >
                  <template v-slot:dropdownButton>
                    <img :src="'/storage/icons/clock.png'" />
                  </template>
                </VueTimepicker>

                <div class="mt-2 text-red-600 text-sm" v-if="errors.start">
                  {{ errors.start }}
                </div>
              </div>

              <div class="mt-8">
                <Label for="type_id" value="Behandlungstyp" />
                <Select
                  id="type_id"
                  class="mt-1 block w-full"
                  v-model="form.type_id"
                  required
                  @change="changeType"
                >
                  <option disabled value="">Behandlungstyp auswählen</option>

                  <option v-for="type in types" :key="type.id" :value="type.id">
                    {{ type.name }}
                  </option>
                </Select>
                <div class="mt-2 text-red-600 text-sm" v-if="errors.type_id">
                  {{ errors.type_id }}
                </div>
              </div>

              <div class="mt-8">
                <Label for="duration" value="Dauer" />
                <Select
                  id="duration"
                  class="mt-1 block w-full"
                  v-model="form.duration"
                  required
                >
                  <option disabled value="">Dauer auswählen</option>

                  <!--<option v-for="n in 10" :key="n" :value="n">
                                        {{ `${n} Einheiten` }} -
                                        {{ n * interval }} Min.
                                    </option>-->
                  <option
                    v-for="(iv, n) in interval_values"
                    :key="n"
                    :value="n"
                  >
                    {{ `${n} Einheiten` }} - {{ iv }} Min.
                  </option>
                </Select>
                <div class="mt-2 text-red-600 text-sm" v-if="errors.duration">
                  {{ errors.duration }}
                </div>
              </div>

              <div class="mt-8">
                <Label for="room_id" value="Behandlungsraum" />
                <Select
                  id="room_id"
                  class="mt-1 block w-full"
                  v-model="form.room_id"
                  required
                >
                  <option disabled class="opacity-70" value="">
                    Behandlungsraum auswählen
                  </option>

                  <option
                    v-for="room in rooms"
                    :key="room.id"
                    :value="room.id"
                    :disabled="disabledRooms.includes(room.id)"
                  >
                    {{ room.name }}
                  </option>
                </Select>
                <div class="mt-2 text-red-600 text-sm" v-if="errors.room_id">
                  {{ errors.room_id }}
                </div>
              </div>
            </div>

            <div class="w-[30%] pl-16 pb-8">
              <div class="">
                <Label for="user_id" value="Behandler/in" />
                <Select
                  id="user_id"
                  class="mt-1 block w-full"
                  v-model="form.practitioner_id"
                  required
                >
                  <option disabled selected value="">
                    Behandler/in auswählen
                  </option>

                  <option
                    v-for="user in practitioners"
                    :key="user.id"
                    :value="user.id"
                  >
                    {{ user.fullname }}
                  </option>
                </Select>
                <div
                  class="mt-2 text-red-600 text-sm"
                  v-if="errors.practitioner_id"
                >
                  {{ errors.practitioner_id }}
                </div>
              </div>

              <div class="mt-8">
                <Label for="equipment" value="Werkzeuge/Materialen" />
                <Textarea
                  id="equipment"
                  class="mt-1 block w-full resize-none"
                  style="height: 3.9rem"
                  v-model="form.equipment"
                  required
                />
                <div class="mt-2 text-red-600 text-sm" v-if="errors.equipment">
                  {{ errors.equipment }}
                </div>
              </div>

              <div class="mt-8">
                <Label for="description" value="Anmerkungen" />
                <Textarea
                  id="description"
                  class="mt-1 block w-full resize-none"
                  style="height: 3.9rem"
                  v-model="form.description"
                  required
                />
                <div
                  class="mt-2 text-red-600 text-sm"
                  v-if="errors.description"
                >
                  {{ errors.description }}
                </div>
              </div>

              <div class="mt-8 flex justify-between">
                <div>
                  <input
                    id="send_mail_on_create"
                    v-model="form.send_mail_on_create"
                    type="checkbox"
                  />
                  <label
                    class="ml-2 text-sm font-bold"
                    for="send_mail_on_create"
                    >E-Mail Versand</label
                  >
                </div>

                <div v-if="showSMS">
                  <input
                    id="send_sms_on_create"
                    v-model="form.send_sms_on_create"
                    type="checkbox"
                  />
                  <label class="ml-2 text-sm font-bold" for="send_sms_on_create"
                    >SMS Versand</label
                  >
                </div>

                <div>&nbsp;</div>
              </div>

              <div class="mt-8">
                <Label for="stati" value="Status" />

                <div class="flex gap-4 mt-1 relative">
                  <StatiSearch
                    :class="{
                      'opacity-60': form.customStati,
                    }"
                    :stati="stati"
                    :currentStatus="0"
                    v-model="form.status"
                    @selectStatus="form.status = $event"
                  />
                </div>

                <div class="mt-2 text-red-600 text-sm" v-if="errors.status">
                  {{ errors.status }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
